<template>
    <div class="d-flex flex-column">
        <v-row class="ma-0 justify-space-between mt-6 mb-3 align-center">
            <v-col cols="12" md="5" class="px-0">
                <v-text-field 
                    v-model="localFilters.search" 
                    :label="`Filtar ${props.competencyLabel}`" 
                    prepend-inner-icon="mdi-magnify"
                    :loading="isLoading"
                    clearable
                    hide-details
                    @click:prepend-inner="fetchTargetCompetencies(localFilters)"
                    @click:clear="fetchTargetCompetencies(localFilters)"
                    @keyup="scheduleCompetencySearch"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="auto" class="px-0">
                <v-btn 
                    prepend-icon="mdi-plus" 
                    color="primary" 
                    @click="openDialogForAction('create')"
                >
                    Criar Competência
                </v-btn>
            </v-col>
        </v-row>
        <v-data-table-server
            v-model:items-per-page="localFilters.per_page"
            v-model:page="localFilters.page"
            :headers="tableHeaders"
            :items="competenciesList"
            :items-length="localFilters.total ?? 0"
            :loading="isLoading"
            :loading-text="
                firstLoad 
                ? 'Obtendo lista de competências...' 
                : 'Atualizando lista de competências...'
            "
            item-value="id"
            :no-data-text="
                hasFailed 
                ? `Ocorreu um erro a obter a lista de ${props?.competencyLabel ? props.competencyLabel : 'competências'}.` 
                : `Não existem ${props?.competencyLabel ? props.competencyLabel : 'competências'} de acordo com os parâmetros de pesquisa.`
            "
            :mobile="mdAndDown"
            hide-default-footer
            @update:sort-by="fetchTargetCompetencies(localFilters)"
            show-expand
        >
            <template #item.competency="{item}">
                <div class="d-flex flex-column">
                    <span class="font-weight-bold">
                        {{ item?.name ?? "Competência sem Nome" }}
                    </span>
                    <span>
                        {{ item?.description ?? "Competência sem descrição" }}
                    </span>
                </div>
            </template>
            <!-- <template #item.indicators_count="{ item }">
                <v-chip color="primary" variant="tonal">
                    {{ item.indicators_count ?? 0 }}
                </v-chip>
            </template> -->
            <template #item.actions="{item}">
                <div class="d-flex flex-column flex-md-row justify-lg-end ga-4 align-center">
                    <v-tooltip
                        v-if="item?.can_update"
                        text="Editar Competência"
                        location="top"
                        :disabled="isLoading"
                    >
                        <template #activator="{props}">
                            <v-btn
                                v-bind="props"
                                icon="mdi-pencil"
                                color="dark"
                                rounded="xl"
                                size="x-small"
                                :disabled="isLoading"
                                @click="openDialogForAction('update', item)"
                            ></v-btn>
                        </template>
                    </v-tooltip>
                    <v-tooltip 
                        v-if="item?.can_delete"
                        text="Apagar Competência"
                        :disabled="isLoading"                        
                    >
                        <template #activator="{props}">
                            <v-btn
                                v-bind="props"
                                icon="mdi-delete"
                                color="error"
                                rounded="xl"
                                size="x-small"
                                :disabled="isLoading"                                
                                @click="openDialogForAction('delete', item)"
                            ></v-btn>
                        </template>
                    </v-tooltip>
                </div>
            </template>
            <template #item.data-table-expand="{ internalItem, isExpanded, toggleExpand }">
                <v-chip 
                    v-if="internalItem.raw.indicators_count == 0"
                    color="primary" variant="tonal">0</v-chip>
                <v-btn
                    v-else
                    :text="`${internalItem.raw.indicators_count}`"
                    class="text-none"
                    color="medium-emphasis"
                    size="small"
                    variant="tonal"
                    :disabled="isLoading"
                    :prepend-icon="isExpanded(internalItem) ? 'mdi-eye-off-outline' : 'mdi-eye'"
                    :append-icon="isExpanded(internalItem) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                    slim
                    @click="toggleCompetencyIndicators(internalItem, isExpanded, toggleExpand)"
                ></v-btn>
            </template>
            <template #expanded-row="{ columns, item }">
                <td :colspan="columns.length" class="pa-0">
                    <v-expand-transition>
                        <v-progress-linear
                            v-if="indicatorLoadingById[item.id]"
                            color="primary"
                            indeterminate
                        />
                    </v-expand-transition>

                    <v-alert
                        v-if="indicatorErrorsById[item.id]"
                        class="ma-3"
                        type="error"
                        variant="tonal"
                    >
                        Não foi possível obter os indicadores.
                    </v-alert>

                    <ul class="ms-6 mt-2">
                        <li 
                            v-for="indicator in indicatorsByCompetencyId[item.id]" 
                            :key="item.id" 
                            class="mx-6"
                        >
                            {{ indicator?.description ?? indicator?.name ?? 'Indicador sem descrição' }}
                        </li>
                    </ul>
                    <hr
                        v-if="
                            !indicatorLoadingById[item.id] 
                            && indicatorsByCompetencyId?.[item.id]?.length
                        "
                        class="mt-4 mb-6 mx-auto">
                </td>
            </template>
        </v-data-table-server>
        <div class="d-flex justify-end align-center">
            <strong>{{ localFilters.from ?? '??' }} - {{ localFilters.to ?? '??' }} / {{ localFilters.total ?? '??' }}</strong>
            <v-pagination
                v-model="localFilters.page"
                :length="localFilters.last_page ?? 1"
                :total-visible="0"
                @update:model-value="fetchTargetCompetencies(localFilters)"
            />
        </div>
    </div>
</template>
<script setup>
import { onMounted, ref, onBeforeUnmount, readonly } from 'vue';
import { useDisplay } from 'vuetify';
import Indicators from '@/api/assessments/competencies/Indicators';
import manipulators from "@/composables/stringManipulations.js"
import CompetencyForm from '@/components/assessments/competencies/CompetencyForm.vue';
import IndicatorForm from '@/components/assessments/competencies/IndicatorForm.vue';
import formValidationRules from '../../../composables/formValidationRules';
import {useDialog} from "@internal_projects_dd/universal-dialog";
import TechnicalCompetencies from '@/api/assessments/competencies/TechnicalCompetencies';
import PersonalCompetencies from '@/api/assessments/competencies/PersonalCompetencies';

const props = defineProps({
    competencyType: {
        type: String,
        required: true
    },
    competencyLabel: {
        type: String,
        default: "Competências",
    },

    fetchMethod: {
        type: Function,
        default: async () => {},
    },
    filters : {
        type: Object,
        default: null,
    },
});

const dialog = useDialog();
const isLoading = ref(false);
const failedLoading = ref(false);
const competenciesList = ref([]);
const { mdAndDown } = useDisplay();
const searchTimeoutId = ref(null);
const firstLoad = ref(true);
const hasFailed = ref(false);

const indicatorLoadingById = ref({});
const indicatorErrorsById = ref({});
const indicatorsByCompetencyId = ref({});

const localFiltersTemplate = {
    direction: "asc",
    from: 0,
    last_page: 1,
    order_by: "name",
    page: 1,
    per_page: 10,
    search: null,
    to: 0,
    total: 0,
};
const localFilters = ref(props.filters ?? {...localFiltersTemplate});

const tableHeaders = [
    {
        title: "Competência",
        align: "start",
        sortable: true,
        key: "competency"
    },
    {
        title: "Indicadores",
        align: "end",
        sortable: true,
        key: "data-table-expand",
    },
    {
        title: "Acções",
        align: "start",
        sortable: false,
        key: "actions"
    },
];


onMounted(() => {
    fetchTargetCompetencies(localFilters);
});

async function fetchTargetCompetencies(filters) {
    isLoading.value = true;
    failedLoading.value = false;

    

    props.fetchMethod(filters ?? localFilters.value)
    ?.then((response) => {
        competenciesList.value = Array.isArray(response?.data?.data)
                                    ? response.data.data
                                    : (
                                        Array.isArray(response?.data)
                                        ? response.data
                                        : []
                                    );

        localFilters.value = {
            ...localFilters.value,
            page: response?.data?.current_page ?? localFilters.value.page,
            per_page: response?.data?.per_page ?? localFilters.value.per_page,
            last_page: response?.data?.last_page ?? 1,
            total: response?.data?.total ?? 0,
            from: response?.data?.from ?? 0,
            to: response?.data?.to ?? 0,
        };
        
    })
    ?.catch((error) => {
        hasFailed.value = true;
        console.error(error);
        throw error;
    })
    ?.finally(() => {
        isLoading.value = false;
        if(firstLoad.value) {
            firstLoad.value = false
        }
    });

    // if fetchMethod prop is not a Promise (does not have a finally())
    // set isLoading back to false to avoid it being stuck as true
    if([undefined, null].includes(typeof props?.fetchMethod?.finally)) {
        isLoading.value = false;
        if(firstLoad.value) {
            firstLoad.value = false
        }
    }
}

function scheduleCompetencySearch() {
    if (searchTimeoutId.value !== null) {
        clearTimeout(searchTimeoutId.value);
    }

    searchTimeoutId.value = setTimeout(() => {
        fetchTargetCompetencies(localFilters.value);
        searchTimeoutId.value = null;
    }, 500);
}

async function toggleCompetencyIndicators(
    internalItem,
    isExpanded,
    toggleExpand,
) {

    if (isExpanded(internalItem)) {
        toggleExpand(internalItem);
        return;
    }

    const competency = internalItem.raw;
    const competencyId = competency?.id;
    
    if(!competencyId) {
        return;
    }

    toggleExpand(internalItem);

    if (
        indicatorLoadingById.value[competencyId]
        || indicatorsByCompetencyId.value[competencyId] !== undefined
    ) {
        return;
    }

    indicatorLoadingById.value = {
        ...indicatorLoadingById.value,
        [competencyId]: true,
    };

    try {
        const response = await Indicators.indicators(
            props.competencyType,
            competencyId,
        );

        indicatorsByCompetencyId.value = {
            ...indicatorsByCompetencyId.value,
            [competencyId]: Array.isArray(response?.data)
                ? response.data
                : [],
        };
    } catch (error) {
        indicatorErrorsById.value = {
            ...indicatorErrorsById.value,
            [competencyId]: true,
        };

        console.error("Erro ao obter os indicadores da competência:", error);
    } finally {
        indicatorLoadingById.value = {
            ...indicatorLoadingById.value,
            [competencyId]: false,
        };
    }

}

async function openDialogForAction(action, competencyId = null) {

    if(!["new", "create"].includes(action) && competencyId === null) {
        throw new RangeError(
            `Only the create/new action allows not passing a competency, got '${action}'.`
        )
    }

    const idempotency_key = self?.crypto?.randomUUID() ?? null;

    const componentProperties = resolveComponentPropertiesForAction(action, competencyId);
    const actionSpecificDialogOptions = resolveDialogOptionsForAction(action);

    componentProperties.idempotencyKey = idempotency_key;
    componentProperties.onSubmit = createCompetencySubmitHandler(action, competencyId);

    await dialog.open({

        component: CompetencyForm,

        props : {
            ...componentProperties
        },
        options: {
            
            ...actionSpecificDialogOptions
        }
    })
}

function resolveComponentPropertiesForAction(action = null, competency = null) {
    const properties = {
        competency: null,
        readonly: false,
        action: null,
        
    };

    switch (action) {
        case "edit":
        case "update":
        case "delete":
        case "details":
        case "read":
        case "get":

            if(!competency) {
                throw new TypeError(
                    `You need to provide a request id for the '${action}' action.`,
                    {
                        cause: "ValueError",
                    }
                )
            }

            properties.competency = {...competency};
            
            break;
        case "create":
            
            break;
        default:

            throw new RangeError(
                `Invalid action: '${action}'!`
            );
    }

    properties.action = action;

    if(!["create", "new", "edit", "update"].includes(action)) {
        properties.readonly = true;
    }

    properties.competencyType = props.competencyType;

    return properties;
}

function resolveDialogOptionsForAction(action = null) {
    const dialogOptions = {
        titleBackgroundColor: "transparent",
        titleTextColor: "primary",
        persistent: true,
        maxWidth: "960",
        showCancel: false,
        onConfirm: "submit",
        confirmActionColor: "primary",
        onConfirmFailureExpectedResult: false,
        onConfirmFailureThrow: true,
    };

    let uiActionVerb;
    let uiActionAdjective;
    let uiActionUnderWayLabel;

    // define ui labels, icon(s) and API method to use
    switch (action) {

        case "create":
        case "new":
            
            uiActionVerb = "criar";
            uiActionAdjective = "criado";
            uiActionUnderWayLabel = "criando";

            dialogOptions.confirmActionIcon = "mdi-plus";
            
            break;

        case "edit":
        case "update":
            
            uiActionVerb = "atualizar";
            uiActionAdjective = "atualizado";
            uiActionUnderWayLabel = "atualizando";

            dialogOptions.confirmActionIcon = "mdi-content-save";
            
            break;

        case "details":
        case "read":
        case "get":

            dialogOptions.onConfirm = null;
            dialogOptions.confirmActionIcon = "mdi-close";
            dialogOptions.persistent = false;
            dialogOptions.title = `Detalhes da ${props.competencyLabel}`;
            dialogOptions.confirmActionText = `Fechar ${props.competencyLabel}`
            dialogOptions.onConfirmActionText = `Fechando ${props.competencyLabel}`;

            dialogOptions.onConfirm = async (detailsDialog) => {
                return true;
            }

            break;

        case "delete":
            
            uiActionVerb = "apagar";
            uiActionAdjective = "apagado";
            uiActionUnderWayLabel = "apagando";

            dialogOptions.confirmActionIcon = "mdi-delete";
            dialogOptions.confirmActionColor = "error";

            break;

        default:
            throw new RangeError(
                `Invalid action: '${action}'!`
            )
    };

    if(["get", "read"].includes(action)) {
        return dialogOptions;
    }

    // set shared dynamic properties
    dialogOptions.title = `${manipulators.firstLetterToUpperCase(uiActionVerb)} ${props.competencyLabel}`;
    dialogOptions.confirmActionText = `${manipulators.firstLetterToUpperCase(uiActionVerb)} ${props.competencyLabel}`;
    dialogOptions.onConfirmActionText = `${manipulators.firstLetterToUpperCase(uiActionUnderWayLabel)} ${props.competencyLabel}...`;
    
    dialogOptions.onConfirmSuccess = async (result) => {
        fetchTargetCompetencies();

        await openSuccessDialog(
            `${props.competencyLabel} ${uiActionAdjective} com sucesso!`
        );

        return result;
    }

    dialogOptions.onConfirmError = async (error, currentDialog) => {
        console.error(
            `Erro a ${uiActionVerb} ${props.competencyLabel}:`,
            String(error)
        );

        currentDialog.options.onConfirmFailedErrorTitle = 
                    error?.reason
                    ?? `Erro a ${uiActionVerb} ${props.competencyLabel}:`;

        currentDialog.options.onConfirmFailedErrorMessage = 
            (Object.values(error?.response?.data?.errors ?? {})?.flat().join("\n")
            || error?.response?.data?.message)
            ?? error?.message
            ?? String(error)
            ?? `Não foi possível ${uiActionVerb} a ${props.competencyLabel}! Tente novamente`;
    }


    return dialogOptions;
}

function createCompetencySubmitHandler(action, competency = null) {
    const api = props.competencyType === "technical"
        ? TechnicalCompetencies
        : PersonalCompetencies;

    const competencyId = typeof competency === "object"
        ? competency?.id
        : competency;

    switch (action) {
        case "create":
        case "new":
            return async (payload) => api.create(payload);

        case "edit":
        case "update":
            return async (payload) => api.update(competencyId, payload);

        case "delete":
            return async () => api.delete(competencyId);

        default:
            throw new RangeError(`Unsupported competency action: ${action}.`);
    }
}



async function openSuccessDialog(bodyText) {
    await dialog.open({
        options: {
            bodyText,
            persistent: true,
            maxWidth: 500,
            showCancel: false,
            confirmActionIcon: "",
            confirmActionText: "Ok",
            confirmActionColor: "primary",
        },
    });
}


onBeforeUnmount(() => {
    if (searchTimeoutId.value !== null) {
        clearTimeout(searchTimeoutId.value);
    }
});
</script>